/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable-next-line react/prop-types */
import { useState, useRef, useEffect } from 'react';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import {
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {
  ArrowDropDownCircleOutlined as ArrowDropDownCircleOutlinedIcon,
  CheckBoxOutlined as CheckBoxOutlinedIcon,
  CloseOutlined as CloseOutlinedIcon,
  CropOriginalOutlined as CropOriginalOutlinedIcon,
  PinOutlined as PinOutlinedIcon,
  NotesOutlined as NotesOutlinedIcon,
  RadioButtonCheckedOutlined as RadioButtonCheckedOutlinedIcon,
  ShortTextOutlined as ShortTextOutlinedIcon,
  SmartDisplayOutlined as SmartDisplayOutlinedIcon,
} from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import { Editor } from 'react-draft-wysiwyg';
import {
  EditorState,
  ContentBlock,
  ContentState,
  convertToRaw,
  convertFromHTML,
  convertFromRaw,
  EntityRange,
  InlineStyleRange,
} from 'draft-js';
import ReactHtmlParser from 'react-html-parser';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { BASE_FONT_FAMILY, PALETTE } from '~/assets/styles/base';
import {
  ORIGINAL_FORM_LABEL_STYLES,
  ORIGINAL_FORM_VALIDATION,
  OriginalFormLabelStylesType,
  QUESTION_CONFIG_TYPE,
  QUESTION_TYPE_ID,
  QuestionConfigType,
} from '~/common/constants';
import {
  removeTagsOnQuestionLabel,
  getOriginalFormsValidationMsg,
  escapeQuestionsContentLabel,
} from '~/common/functional';
import {
  QuestionProps,
  OriginalFormQuestionIdMap,
  MultipleAnswerConfigProps,
  SingleAnswerConfigProps,
  MultipleAnswerConfigAnswerProps,
  SingleAnswerConfigAnswerProps,
} from '~/common/types';
import InputValidationErrorTooltip from '~/components/ui-parts/InputValidationErrorTooltip';
import ComponentControl from '../ComponentControl';
import ShortAnswer from '../ShortAnswer';
import LongAnswer from '../LongAnswer';
import SimpleAnswer from '../SimpleAnswer';
import MultipleAnswer from '../MultipleAnswer';
import ImageUploadModal from '../ImageUploadModal';
import VideoUploadModal from '../VideoUploadModal';
import NumericAnswer from '../NumericAnswer';
import { LOCALE_STRING } from '~/constants';
import { useDebounce } from '~/hooks';

type ContentBoxProps = {
  $isActive?: boolean;
  $isBoxFormValid?: boolean;
};
const ContentBox = styled.div<ContentBoxProps>`
  width: 768px;
  padding: 10px 25px;
  margin-top: 40px;
  border: 1px solid ${(props) => (props.$isActive ? PALETTE.primary.main : PALETTE.border.frame)};
  background: ${PALETTE.white};
  display: flex;
  flex-direction: column;
  ${(props) => {
    if (!props.$isBoxFormValid) {
      return `
        border: 1px solid ${PALETTE.error.main};
      `;
    }
    if (props.$isActive) {
      return `
      border: 1px solid ${PALETTE.primary.main};
      `;
    }
    return `
      border: 1px solid ${PALETTE.border.frame};
    `;
  }}
`;

type PageNumInfoBoxProps = {
  $isActive?: boolean;
  $isBoxFormValid?: boolean;
};
const PageNumInfoBox = styled.div<PageNumInfoBoxProps>`
  background: ${(props) => (props.$isActive ? PALETTE.primary.subButton : PALETTE.blue.main)};
  border: 1px solid ${(props) => (props.$isActive ? PALETTE.primary.main : PALETTE.border.frame)};
  height: 32px;
  width: 768px;
  margin: -26px 0 24px -26px;
  padding-left: 14px;
  padding-top: 6px;
  &:hover {
    cursor: move;
  }
  ${(props) => {
    if (!props.$isBoxFormValid) {
      return `
        border: 1px solid ${PALETTE.error.main};
        background: ${PALETTE.error.light};
      `;
    }
    if (props.$isActive) {
      return `
      border: 1px solid ${PALETTE.primary.main};
      background: ${PALETTE.primary.subButton};
      `;
    }
    return `
      border: 1px solid ${PALETTE.border.frame};
      background: ${PALETTE.blue.main};
    `;
  }}
`;

const FormControlStyle = styled(FormControl)`
  position: absolute;
  margin: 24px 20px 10px 400px;
  width: 304px;
`;

const StyledMenuItem = styled(MenuItem)`
  font-size: 14px;
  color: ${PALETTE.text.main};
  font-family: ${BASE_FONT_FAMILY};
  margin-bottom: 10px;
  width: 100%;
`;

const SurveyDesc = styled.div`
  padding: 12px 0 8px;
  display: inline-flex;
  justify-content: space-between;
`;

type EditorAreaProps = {
  $isQuestionValid?: boolean;
};
const EditorArea = styled.div<EditorAreaProps>`
  margin-top: 30px;
  position: relative;
  .public-DraftStyleDefault-block {
    line-height: 1.5;
  }
  .rdw-link-modal {
    height: initial;
  }
  .rdw-link-modal-input {
    font-size: 13px;
    height: 35px;
  }
  .public-DraftStyleDefault-block {
    margin: 0px 0 !important;
  }
  ${(props) =>
    !props.$isQuestionValid &&
    css`
      .rdw-editor-main {
        padding-bottom: 5px;
        border-bottom: 1px solid ${PALETTE.error.main} !important;
      }
    `}
`;

const ImageUploadArea = styled.div`
  margin: 50px 10px 0 0;
`;

const ImgVideoPreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 20px;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
`;

const StyledImg = styled.img`
  max-height: 170px;
`;

type Props = {
  questionIdx: number;
  isActive?: boolean;
  currentSection: number;
  totalSection: number;
  totalQuestions: number;
  questionData: QuestionProps;
  itemIdx: number;
  questionIds: Array<OriginalFormQuestionIdMap>;
  handleContentChange: Function;
  handleDuplicateQuestion: Function;
  handleDeleteQuestion: Function;
  handleIsActiveElement: Function;
  isValidationExecuted?: boolean;
  handleFormValidationError: Function;
  isGotoAnswerEnabled?: boolean;
  id: string;
};

const QuestionBox: React.FC<Props> = ({
  questionIdx,
  isActive,
  currentSection,
  totalSection,
  totalQuestions,
  questionData,
  itemIdx,
  questionIds,
  handleContentChange,
  handleDuplicateQuestion,
  handleDeleteQuestion,
  handleIsActiveElement,
  isValidationExecuted,
  handleFormValidationError,
  isGotoAnswerEnabled,
  id,
}): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  const { t } = useTranslation();

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
  });

  const styleDnd: React.CSSProperties = {
    transform: CSS.Transform.toString(transform)?.replace(/scaleY\([^)]*\)\s*/, ''),
    transition,
  };

  const initSimpleAnswerConfig = {
    random: false,
    gotoRequired: false,
    answers: Array(1).fill({
      label: '',
      goto: '',
      isValid: true,
    }),
  };

  const initMultipleAnswerConfig = {
    random: false,
    answers: Array(1).fill({
      label: '',
      isValid: true,
    }),
  };

  const customTranslations = {
    'components.controls.link.linkTitle': t('editor.link.linkTitle'),
    'components.controls.link.linkTarget': t('editor.link.linkTarget'),
    'components.controls.link.linkTargetOption': t('editor.link.linkTargetOption'),
    'generic.add': t('editor.link.btnAdd'),
    'generic.cancel': t('editor.link.btnCancel'),
  };

  const [questionContent, setQuestionContent] = useState<QuestionProps>(questionData);

  const questionBoxId = questionContent.id;

  const [editorState, setEditorState] = useState(() =>
    EditorState.createWithContent(ContentState.createFromText(questionData.label))
  );
  const [newQuestionContentHtml, setNewQuestionContentHtml] = useState('');

  const [showImageUploadModal, setShowImageUploadModal] = useState<boolean>(false);
  const [showVideoUploadModal, setShowVideoUploadModal] = useState<boolean>(false);

  const [imgPreview, setImgPreview] = useState<JSX.Element[] | undefined>();
  const [videoPreview, setVideoPreview] = useState<JSX.Element | undefined>();
  const [videoUrl, setVideoUrl] = useState('');
  const debouncedValue = useDebounce(editorState, 100);
  const [prevHtmlContent, setPrevHtmlContent] = useState(questionData.label);
  const [isBoxFormValid, setIsBoxFormValid] = useState(true);

  type LinkProps = {
    title: string;
    target: string;
    targetOption: string;
  };
  const [editorLinks, setEditorLinks] = useState<LinkProps[]>([]);
  /* istanbul ignore next callBackテストが難しいため */
  const handleLinkCallback = (linkValue: LinkProps) => {
    const newObject = {
      title: linkValue.title,
      target: linkValue.target,
      targetOption: linkValue.targetOption,
    };
    if (newObject.target) setEditorLinks((newArray) => [...newArray, newObject]);
    return linkValue;
  };

  let startTag = '';
  const styleText = (style: OriginalFormLabelStylesType) => {
    if (style === ORIGINAL_FORM_LABEL_STYLES.BOLD) {
      startTag = `<b>`;
    } else if (style === ORIGINAL_FORM_LABEL_STYLES.ITALIC) {
      startTag = `<i>`;
    } else if (style === ORIGINAL_FORM_LABEL_STYLES.UNDERLINE) {
      startTag = `<u>`;
    }
  };

  type InlineEvent = {
    start: number;
    length: number;
    tag: string;
  };
  type Part = {
    char: string;
    openTags: string[];
    closingTags: string[];
  };

  const effectEnd = (effect: string): string => effect.replace('<', '</');
  const applyEffect = (text: string, events: InlineEvent[]): string => {
    const parts: Part[] = text.split('').map((c) => ({ char: c, openTags: [], closingTags: [] }));

    events.forEach((event) => {
      const { start, length, tag } = event;
      parts[start].openTags.push(tag);
      parts[start + length - 1].closingTags.push(tag);
    });

    let html = '';
    parts.forEach((part) => {
      part.openTags.sort().reverse();
      part.closingTags.sort();

      part.openTags.forEach((tag) => {
        html += tag;
      });

      html += part.char;
      part.closingTags.forEach((tag) => {
        html += effectEnd(tag);
      });
    });
    return html;
  };

  const exportToHTML = (newEditorState: typeof EditorState) => {
    const contentState = newEditorState.getCurrentContent();
    const rawContentState = convertToRaw(contentState);

    let formattedText = '';
    rawContentState.blocks.map((block: typeof ContentBlock, index: number) => {
      const { text, entityRanges, inlineStyleRanges } = block;

      const inlineStyleEvents: InlineEvent[] = [];
      inlineStyleRanges.forEach((range: typeof InlineStyleRange) => {
        const { offset, length, style } = range;
        styleText(style);

        const event: InlineEvent = {
          start: offset,
          length,
          tag: startTag,
        };
        inlineStyleEvents.push(event);
      });
      let formattedBlock = applyEffect(text, inlineStyleEvents);

      entityRanges.forEach((entity: typeof EntityRange) => {
        const { offset, length } = entity;
        const linkTitle = text.slice(offset, offset + length);
        editorLinks.forEach((linkValue) => {
          if (linkTitle === linkValue.title) {
            const link = `<a href="${linkValue.target}" target="${linkValue.targetOption}">${linkTitle}</a>`;
            formattedBlock = formattedBlock.replace(linkTitle, link);
          }
        });
      });

      formattedBlock = formattedBlock.replaceAll('\n', '<br>');
      formattedText += `${formattedBlock}`;
      /* istanbul ignore next editorに入力する操作のテストが難しい */
      formattedText += index === rawContentState.blocks.length - 1 ? '' : `<br>`;
      return block;
    });

    setNewQuestionContentHtml(formattedText);
    return formattedText;
  };

  const selectQuestionTypeOptions = () => {
    const pullDownOptions = t('registration.originalForm.manual.selectOption', {
      returnObjects: true,
    });

    const options: JSX.Element[] = [];
    const iconStyle = { fontSize: 22, marginRight: 4, marginBottom: -1.5 };

    Object.entries(pullDownOptions).forEach(([key, value]) => {
      const qType = Number(key);
      let optionIcon;

      switch (qType) {
        case QUESTION_TYPE_ID.SINGLE_ANSWER:
          optionIcon = <RadioButtonCheckedOutlinedIcon sx={iconStyle} />;
          break;
        case QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN:
          optionIcon = <ArrowDropDownCircleOutlinedIcon sx={iconStyle} />;
          break;
        case QUESTION_TYPE_ID.MULTIPLE_ANSWER:
          optionIcon = <CheckBoxOutlinedIcon sx={iconStyle} />;
          break;
        case QUESTION_TYPE_ID.FREE_ANSWER_SHORT:
          optionIcon = <ShortTextOutlinedIcon sx={iconStyle} />;
          break;
        case QUESTION_TYPE_ID.FREE_ANSWER_LONG:
          optionIcon = <NotesOutlinedIcon sx={iconStyle} />;
          break;
        case QUESTION_TYPE_ID.FREE_ANSWER_NUMERIC:
          optionIcon = <PinOutlinedIcon sx={iconStyle} />;
          break;
        default:
          break;
      }

      const styledMenuItem = (
        <StyledMenuItem
          key={`${questionBoxId}-${qType}`}
          value={qType}
          data-testid={qType}
          disabled={qType === 0}
        >
          {optionIcon}
          {t(value)}
        </StyledMenuItem>
      );
      options.push(styledMenuItem);
    });

    return options;
  };

  const updateQuestionContent = (newQuestionContent: QuestionProps, onBlur = true) => {
    setQuestionContent(newQuestionContent);
    handleContentChange(newQuestionContent, itemIdx, onBlur);
  };

  const handleRequiredChange = (checkBoxType: QuestionConfigType) => {
    const newQuestionContent = JSON.parse(JSON.stringify(questionContent));

    if (checkBoxType === QUESTION_CONFIG_TYPE.REQUIRED) {
      newQuestionContent.required = !newQuestionContent.required;
    } else {
      let questionConfig = 'singleAnswerConfig';
      if (questionContent.type === QUESTION_TYPE_ID.MULTIPLE_ANSWER) {
        questionConfig = 'multipleAnswerConfig';
      }

      newQuestionContent[questionConfig][checkBoxType] = !(
        (questionContent as Record<string, any>)[questionConfig] as Record<string, boolean>
      )[checkBoxType];

      // Change goto value when checkbox for goto is checked
      if (checkBoxType === QUESTION_CONFIG_TYPE.GOTO_REQUIRED) {
        const newAnswers = newQuestionContent[questionConfig].answers.map((answer: any) => ({
          ...answer,
          goto: '',
        }));
        newQuestionContent[questionConfig].answers = newAnswers;
      }
    }

    updateQuestionContent(newQuestionContent);
  };

  const handleQuestionTypeChange = (event: SelectChangeEvent) => {
    const questionType = Number(event.target.value);

    if (questionContent.type === questionType) {
      return;
    }

    let newQuestionContent = {
      ...questionContent,
      type: questionType,
      required: false,
    };

    if (
      questionType === QUESTION_TYPE_ID.SINGLE_ANSWER ||
      questionType === QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN
    ) {
      newQuestionContent = {
        ...newQuestionContent,
        singleAnswerConfig: initSimpleAnswerConfig,
      };
    } else if (questionType === QUESTION_TYPE_ID.MULTIPLE_ANSWER) {
      newQuestionContent = {
        ...newQuestionContent,
        multipleAnswerConfig: initMultipleAnswerConfig,
      };
    }

    if (
      questionType !== QUESTION_TYPE_ID.MULTIPLE_ANSWER &&
      newQuestionContent.multipleAnswerConfig
    ) {
      delete newQuestionContent.multipleAnswerConfig;
    }

    if (
      questionType !== QUESTION_TYPE_ID.SINGLE_ANSWER &&
      questionType !== QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN &&
      newQuestionContent.singleAnswerConfig
    ) {
      delete newQuestionContent.singleAnswerConfig;
    }

    updateQuestionContent(newQuestionContent);
  };

  const handleAnswerChange = (
    newAnswersList: MultipleAnswerConfigAnswerProps[] | SingleAnswerConfigAnswerProps[],
    newAnswerConfig: MultipleAnswerConfigProps | SingleAnswerConfigProps,
    onBlur = true
  ) => {
    const newQuestionContent = JSON.parse(JSON.stringify(questionContent));

    let questionConfig = 'multipleAnswerConfig';
    if (
      questionContent.type === QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN ||
      questionContent.type === QUESTION_TYPE_ID.SINGLE_ANSWER
    ) {
      questionConfig = 'singleAnswerConfig';
    }

    if (newAnswerConfig) {
      newQuestionContent[questionConfig] = newAnswerConfig;
    }

    newQuestionContent[questionConfig].answers = newAnswersList;

    updateQuestionContent(newQuestionContent, onBlur);
  };

  const setImgVideoUrlToLabel = (
    htmlContent: string,
    previewVideoData?: JSX.Element,
    previewImgData?: JSX.Element[],
    onBlur = true
  ) => {
    let content = htmlContent;
    if (previewVideoData) {
      content += renderToString(previewVideoData);
    }
    if (previewImgData && previewImgData.length > 0) {
      previewImgData.forEach((data) => {
        content += renderToString(data);
      });
    }
    updateQuestionContent(
      {
        ...questionContent,
        label: content,
        isQuestionValid: true,
      },
      onBlur
    );
  };

  const handleImageUploadSetting = (file?: JSX.Element) => {
    setShowImageUploadModal(false);

    if (file) {
      // Get the current array of file previews
      const currentPreviews = imgPreview ? [...imgPreview] : [];

      if (currentPreviews.length >= ORIGINAL_FORM_VALIDATION.MAX_QUESTION_IMG) {
        // Maximum limit reached
        return;
      }
      // Add the newly uploaded file preview to the array
      const updatedPreview = [...currentPreviews, file];

      setImgPreview(updatedPreview);
      // imgPreview.forEach((data) => {
      setImgVideoUrlToLabel(newQuestionContentHtml, videoPreview, updatedPreview);
      // });
    }
  };

  const handleVideoUploadSetting = (urlValue: string, previewData: JSX.Element) => {
    setShowVideoUploadModal(false);

    setVideoPreview(previewData);
    setVideoUrl(urlValue);
    setImgVideoUrlToLabel(newQuestionContentHtml, previewData, imgPreview);
  };

  const handleOnChangeQuestionLabel = () => {
    if (editorLinks.length > 0) {
      let updatedContent = convertToRaw(editorState.getCurrentContent());

      // Handle space in link
      const newUpdatedContent = updatedContent.blocks;
      editorLinks.forEach((link) => {
        newUpdatedContent.forEach((block: typeof ContentBlock, blockIdx: number) => {
          newUpdatedContent[blockIdx].text = block.text.replace(`${link.title} `, link.title);
        });
      });

      updatedContent = {
        ...updatedContent,
        blocks: newUpdatedContent,
      };

      const newEditorState = EditorState.createWithContent(convertFromRaw(updatedContent));
      setEditorState(newEditorState);
    }
  };

  const showInputError = (tmpIsValidate?: boolean, tmpIsBoxFormValid?: boolean) => {
    if (tmpIsValidate !== undefined && tmpIsValidate) {
      if (tmpIsBoxFormValid !== undefined) {
        return tmpIsBoxFormValid;
      }
    }
    return true;
  };

  const updateQuestionLabelWithoutImg = (index: number, previews: JSX.Element[]) => {
    const currentPreviews = [...previews];
    // Remove the image preview at the specified index
    currentPreviews.splice(index, 1);
    // Update the state variable with the modified array of file previews
    setImgPreview(currentPreviews);
    // Update the label to conation only the files from current previews
    setImgVideoUrlToLabel(newQuestionContentHtml, videoPreview, currentPreviews);
  };

  const updateQuestionLabelWithoutVideo = () => {
    let currentLabel = questionContent.label;

    // Remove iframe tag in currentlabel
    const iFrameTagRegex = /<iframe.*<\/iframe>/g;
    const newLabel = currentLabel.match(iFrameTagRegex);

    if (newLabel !== null) {
      currentLabel = currentLabel.replace(newLabel[0], '');
    }

    updateQuestionContent({
      ...questionContent,
      label: currentLabel,
      isQuestionValid: true,
    });
    setVideoUrl('');
    setVideoPreview(undefined);
  };

  const showInputGotoErrorMsg = () => {
    let errorMsg = t('registration.originalForm.manual.inputBox.validationErrorGoto');
    if (!questionContent.isGotoSectionValid) {
      if (questionContent.singleAnswerConfig?.gotoRequired) {
        const answers = questionContent.singleAnswerConfig?.answers;
        if (answers) {
          const isValidGotoRequired = answers.some((answer) => answer?.goto?.trim().length > 0);
          if (!isValidGotoRequired) {
            errorMsg = t('registration.originalForm.manual.inputBox.validationErrorGotoRequired');
          }
        }
      }
    }
    return errorMsg;
  };

  useEffect(() => {
    let currentLabel = escapeQuestionsContentLabel(questionContent.label);

    const iFrameTagRegex = /<iframe.*<\/iframe>/g;
    let newLabel = currentLabel.match(iFrameTagRegex);

    if (newLabel !== null) {
      setVideoPreview(ReactHtmlParser(newLabel[0])[0]);
      currentLabel = currentLabel.replace(newLabel[0], '');
      setVideoUrl(ReactHtmlParser(newLabel[0])[0].props.src.replace('embed/', 'watch?v='));
    }

    const imgTagRegex = /<img.*\/>/g;
    newLabel = currentLabel.match(imgTagRegex);

    if (newLabel !== null) {
      setImgPreview(ReactHtmlParser(newLabel[0]));
      currentLabel = currentLabel.replace(newLabel[0], '');
    }
    setEditorState(() =>
      EditorState.createWithContent(
        ContentState.createFromBlockArray(convertFromHTML(currentLabel))
      )
    );
    setNewQuestionContentHtml(currentLabel);
    const parser = new DOMParser();
    const doc = parser.parseFromString(currentLabel, 'text/html');
    const aTags = doc.querySelectorAll('a');
    aTags.forEach((aTag) => {
      const newObject = {
        title: aTag.textContent as string,
        target: aTag.getAttribute('href') as string,
        targetOption: aTag.getAttribute('target') as string,
      };
      setEditorLinks((newArray) => [...newArray, newObject]);
    });
  }, []);

  useEffect(() => {
    if (isActive) {
      handleIsActiveElement(itemIdx, ref);
    }
  }, [isActive]);

  useEffect(() => {
    if (!isValidationExecuted) {
      setIsBoxFormValid(true);
    } else {
      const isQuestionValid = questionData.isQuestionValid ?? true;
      const isGotoSectionValid = questionData.isGotoSectionValid ?? true;
      let isValid = false;

      if (
        questionData.type === QUESTION_TYPE_ID.FREE_ANSWER_SHORT ||
        questionData.type === QUESTION_TYPE_ID.FREE_ANSWER_LONG ||
        questionData.type === QUESTION_TYPE_ID.FREE_ANSWER_NUMERIC ||
        questionData.type === QUESTION_TYPE_ID.MULTIPLE_ANSWER ||
        questionData.type === QUESTION_TYPE_ID.TEXT
      ) {
        isValid = isQuestionValid;
      }

      if (
        questionData.type === QUESTION_TYPE_ID.SINGLE_ANSWER ||
        questionData.type === QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN
      ) {
        isValid = isQuestionValid && isGotoSectionValid;
      }

      setIsBoxFormValid(isValid);
    }
    setQuestionContent(questionData);
  }, [questionData]);

  useEffect(() => {
    if (!isBoxFormValid) {
      handleIsActiveElement(itemIdx, ref, true);
    }
  }, [isBoxFormValid]);

  useEffect(() => {
    handleOnChangeQuestionLabel();
  }, [editorLinks]);

  useEffect(() => {
    // Ignore the first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const htmlContent = exportToHTML(debouncedValue);
    if (prevHtmlContent !== htmlContent) {
      setPrevHtmlContent(htmlContent);
      setImgVideoUrlToLabel(htmlContent, videoPreview, imgPreview);
    }
  }, [debouncedValue]);

  return (
    <div ref={setNodeRef}>
      <InputValidationErrorTooltip
        isOneLinerMessage
        error={!questionContent.isGotoSectionValid}
        message={showInputGotoErrorMsg()}
      >
        <ContentBox
          key={`${questionBoxId}-box`}
          data-testid={`questionArea-${questionIdx}`}
          $isActive={isActive}
          $isBoxFormValid={showInputError(
            isValidationExecuted,
            isBoxFormValid && questionContent.isGotoSectionValid
          )}
          ref={ref}
          style={styleDnd}
          onClick={() => handleIsActiveElement(itemIdx, ref)}
        >
          <PageNumInfoBox
            $isActive={isActive}
            $isBoxFormValid={showInputError(
              isValidationExecuted,
              isBoxFormValid && questionContent.isGotoSectionValid
            )}
            data-testid={`questionArea-pageNumInfo-${questionIdx}`}
            {...attributes}
            {...listeners}
          >
            <Typography sx={{ fontSize: 14 }}>{`${currentSection}/${totalSection} ${t(
              'registration.originalForm.manual.pageBreak.page'
            )}`}</Typography>
          </PageNumInfoBox>

          <Typography
            sx={{ fontWeight: 'bold' }}
            variant="title1"
            data-testid={`questionNumInfo-${questionIdx + 1}`}
          >{`Q${questionIdx + 1}.`}</Typography>
          <FormControlStyle size="small">
            <Select
              sx={{
                color: '#022B4D',
                fontSize: 14,
                height: 40,
                '& .MuiSelect-select': {
                  display: 'flex',
                },
              }}
              displayEmpty
              id="questionTypeSelect"
              value={questionContent.type.toString()}
              onChange={handleQuestionTypeChange}
              data-testid="question-type-select"
            >
              {selectQuestionTypeOptions()}
            </Select>
          </FormControlStyle>

          <SurveyDesc>
            <InputValidationErrorTooltip
              customMarginTop="-43px"
              error={!showInputError(isValidationExecuted, questionContent.isQuestionValid)}
              message={t(
                getOriginalFormsValidationMsg(
                  removeTagsOnQuestionLabel(questionContent.label),
                  ORIGINAL_FORM_VALIDATION.QUESTION_LABEL_MAX_SIZE
                ),
                {
                  maxLength: ORIGINAL_FORM_VALIDATION.QUESTION_LABEL_MAX_SIZE.toLocaleString(
                    LOCALE_STRING.ja
                  ),
                }
              )}
            >
              <EditorArea
                $isQuestionValid={showInputError(
                  isValidationExecuted,
                  questionContent.isQuestionValid
                )}
              >
                <Editor
                  localization={{
                    translations: customTranslations,
                  }}
                  toolbar={{
                    options: ['inline', 'link'],
                    inline: {
                      options: ['bold', 'underline', 'italic'],
                    },
                    link: {
                      options: ['link'],
                      linkCallback: /* istanbul ignore next */ (linkValue: LinkProps) =>
                        handleLinkCallback(linkValue),
                      defaultTargetOption: '_blank',
                      showOpenOptionOnHover: false,
                    },
                  }}
                  editorStyle={{
                    paddingBottom: '5px',
                    borderBottom: `1px solid ${PALETTE.border.frame}`,
                    color: PALETTE.text.main,
                  }}
                  wrapperStyle={{
                    width: '600px',
                    display: 'flex',
                    flexDirection: 'column-reverse',
                  }}
                  toolbarStyle={{ width: '250px', border: 'none' }}
                  placeholder={t('registration.originalForm.manual.saComponent.placeholder')}
                  stripPastedStyles
                  editorState={editorState}
                  onEditorStateChange={setEditorState}
                  onBlur={() => {
                    const updatedContent = convertToRaw(editorState.getCurrentContent());
                    const newEditorState = EditorState.createWithContent(
                      convertFromRaw(updatedContent)
                    );
                    setEditorState(newEditorState);
                  }}
                />
              </EditorArea>
            </InputValidationErrorTooltip>
            <ImageUploadArea>
              <IconButton
                onClick={() => {
                  setShowVideoUploadModal(true);
                }}
                sx={{ marginTop: '-10px' }}
                aria-label="upload-url"
              >
                <SmartDisplayOutlinedIcon sx={{ fontSize: 26, color: '#425F77' }} />
              </IconButton>
              <IconButton
                sx={{ marginTop: '-10px' }}
                onClick={() => {
                  setShowImageUploadModal(true);
                }}
                aria-label="upload-image"
              >
                <CropOriginalOutlinedIcon sx={{ fontSize: 26, color: '#425F77' }} />
              </IconButton>
            </ImageUploadArea>
          </SurveyDesc>

          {showImageUploadModal && (
            <ImageUploadModal
              isOpen={showImageUploadModal}
              handleClose={() => setShowImageUploadModal(false)}
              handleSetting={handleImageUploadSetting}
            />
          )}

          {showVideoUploadModal && (
            <VideoUploadModal
              isOpen={showVideoUploadModal}
              handleClose={() => setShowVideoUploadModal(false)}
              handleSetting={handleVideoUploadSetting}
              videoUrl={videoUrl}
            />
          )}

          {videoPreview && (
            <ImgVideoPreviewContainer data-testid="videoPreviewDiv">
              {videoPreview}
              <IconButton
                onClick={() => updateQuestionLabelWithoutVideo()}
                data-testid="deleteVideoButton"
              >
                <CloseOutlinedIcon style={{ fontWeight: 'bold', fontSize: '20px' }} />
              </IconButton>
            </ImgVideoPreviewContainer>
          )}

          {imgPreview &&
            imgPreview.map((preview, index) => (
              <ImgVideoPreviewContainer data-testid={`imgPreviewDiv-${index}`} key={index}>
                <StyledImg src={preview.props.src} alt={preview.props.alt} />
                <IconButton
                  onClick={() => updateQuestionLabelWithoutImg(index, imgPreview)}
                  data-testid={`deleteImgPreviewButton-${index}`}
                >
                  <CloseOutlinedIcon style={{ fontWeight: 'bold', fontSize: '20px' }} />
                </IconButton>
              </ImgVideoPreviewContainer>
            ))}

          {questionContent.singleAnswerConfig &&
            questionContent.type === QUESTION_TYPE_ID.SINGLE_ANSWER && (
              <SimpleAnswer
                questionIdx={questionIdx}
                totalQuestions={totalQuestions}
                isQuestionValid={questionContent.isQuestionValid}
                questionIds={questionIds}
                handleValidation={setIsBoxFormValid}
                handleAnswerChange={handleAnswerChange}
                singleAnswerConfig={questionContent.singleAnswerConfig}
                isValidate={isValidationExecuted}
                handleFormValidationError={handleFormValidationError}
              />
            )}

          {questionContent.singleAnswerConfig &&
            questionContent.type === QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN && (
              <SimpleAnswer
                questionIdx={questionIdx}
                totalQuestions={totalQuestions}
                isPullDown
                isQuestionValid={questionContent.isQuestionValid}
                questionIds={questionIds}
                handleValidation={setIsBoxFormValid}
                handleAnswerChange={handleAnswerChange}
                singleAnswerConfig={questionContent.singleAnswerConfig}
                isValidate={isValidationExecuted}
                handleFormValidationError={handleFormValidationError}
              />
            )}

          {questionContent.multipleAnswerConfig &&
            questionContent.type === QUESTION_TYPE_ID.MULTIPLE_ANSWER && (
              <MultipleAnswer
                questionIdx={questionIdx}
                totalQuestions={totalQuestions}
                isQuestionValid={questionContent.isQuestionValid}
                handleValidation={setIsBoxFormValid}
                handleAnswerChange={handleAnswerChange}
                multipleAnswerConfig={questionContent.multipleAnswerConfig}
                isValidate={isValidationExecuted}
                handleFormValidationError={handleFormValidationError}
              />
            )}

          {questionContent.type === QUESTION_TYPE_ID.FREE_ANSWER_SHORT && <ShortAnswer />}

          {questionContent.type === QUESTION_TYPE_ID.FREE_ANSWER_LONG && <LongAnswer />}

          {questionContent.type === QUESTION_TYPE_ID.FREE_ANSWER_NUMERIC && <NumericAnswer />}

          <Divider sx={{ marginTop: 6.25 }} />

          <ComponentControl
            questionIdx={questionIdx}
            itemIdx={itemIdx}
            questionType={questionContent.type}
            isRequired={questionContent.required}
            isRandom={
              questionContent.type === QUESTION_TYPE_ID.MULTIPLE_ANSWER
                ? questionContent.multipleAnswerConfig?.random
                : questionContent.singleAnswerConfig?.random
            }
            isGotoRequired={questionContent.singleAnswerConfig?.gotoRequired}
            handleRequiredChange={handleRequiredChange}
            handleDuplicateQuestion={handleDuplicateQuestion}
            handleDeleteQuestion={handleDeleteQuestion}
            isGotoAnswerEnabled={isGotoAnswerEnabled}
          />
        </ContentBox>
      </InputValidationErrorTooltip>
    </div>
  );
};

export default QuestionBox;
